


import dayjs, { Dayjs } from 'dayjs'

import { runsheetShiftTablesSelector } from '@/entities/gridTables/selectors/runsheetShiftTablesSelector'
import { runsheetShiftsSelectors, StopTypeEnum } from '@/entities/schedules'
import { openRightSidebar } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { OperationsGanttWidgetWindow } from '@/entities/widgetWindows/models/WidgetWindow'
import { GanttRow, TimelineStop, TravelLeg } from '@/shared/components/GanttChart/models/GanttRow'
import { WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { timeHhMmToMins, timeWithTodayToDayJs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'

export type RunsheetShiftTableData = {
  rows: GanttRow[]
  isReadOnly: boolean
  isSolverRunning: false,
  minStartTime: Dayjs
  maxEndTime: Dayjs
}

export type useRunsheetGanttChartDataArgs = {
  widgetId: WidgetTypeWithSuffix
}

export const useRunsheetGanttChartData = ({ widgetId }: useRunsheetGanttChartDataArgs): RunsheetShiftTableData => {
  const dispatch = useAppDispatch()

  const ganttWindowState = useAppSelector(state => widgetWindowsSelector.selectById(state, widgetId))
  const allShifts = useAppSelector(state => runsheetShiftsSelectors.selectAll(state))
  const shiftTableEntities = useAppSelector(state => runsheetShiftTablesSelector.selectEntities(state))

  // Get the selected shifts and the shift table data to display information on the Gantt chart
  const selectedShiftIds = (ganttWindowState as OperationsGanttWidgetWindow)?.shiftIds || []
  const relevantShifts = allShifts.filter((shift) => selectedShiftIds.includes(shift.id))
  const relevantShiftTables = relevantShifts.map((shift) => shiftTableEntities[shift.id])

  // Minimum start time and maximum end time for the Gantt chart
  let minStartTime = dayjs().endOf('day')
  let maxEndTime = dayjs().startOf('day')

  const rows: (GanttRow | null)[] = relevantShiftTables.map((shiftTable) => {
    if (!shiftTable || !shiftTable.rows) return null

    // Curate the stops for the Gantt chart
    const shiftTableRows = shiftTable.rows.filter(row => row.stopType !== StopTypeEnum.LoadIndicator)
    const stops: TimelineStop[] = []
    const legs: TravelLeg[] = []
    let i = 0

    for (i; i < shiftTableRows.length; i++) {
      const row = shiftTableRows[i]

      // Adding to stops array
      const stopStartTime = timeWithTodayToDayJs(row.plannedTime)

      if (row.displayStopType === 'Start' && minStartTime.isAfter(stopStartTime)) {
        minStartTime = minStartTime.set('hour', stopStartTime.hour()).set('minute', stopStartTime.minute())
      }

      if (row.displayStopType === 'Finish' && maxEndTime.isBefore(stopStartTime)) {
        maxEndTime = maxEndTime.set('hour', stopStartTime.hour()).set('minute', stopStartTime.minute())
      }

      const additionalInfo = []

      if (!['Start', 'Finish', 'Break'].includes(row.displayStopType)) {
        additionalInfo.push(...[
          { key: 'ETA', value: row.plannedTime },
          { key: 'Time at Stop', value: row.timeAtStop },
          { key: 'Order Number', value: row.orderNumber },
          { key: 'Order Quantity', value: row.orderQuantity },
          { key: 'Order Weight', value: Math.round(row.weight * 100) / 100 },
          { key: 'Order Volume', value: Math.round(row.volume * 100) / 100 },
        ])

        if (row.isDepot) {
          additionalInfo.push(...[
            { key: 'Depot Name', value: row.customerName },
            { key: 'Depot Address', value: row.customerDisplayAddress },
          ])
        } else {
          additionalInfo.push(...[
            { key: 'Customer Name', value: row.customerName },
            { key: 'Customer Address', value: row.customerDisplayAddress },
          ])
        }
      }

      stops.push({
        id: row.id,
        scheduleId: row.runsheetId,
        orderId: row.orderId,
        shiftId: row.shiftId,
        displayStopType: row.displayStopType,
        startTime: stopStartTime,
        duration: timeHhMmToMins(row.timeAtStop) || 0,
        label: row.sequence.toString(),
        isDraggable: row.isDraggable,
        action: row.stopType === StopTypeEnum.Order && row.isJob ? () => {
          if (row.orderId) {
            dispatch(openRightSidebar())
            dispatch(setDetailViewedOrder({ orderId: row.orderId }))
          }
        } : undefined,
        additionalInfo: additionalInfo,
      })
      
      if (i === 0) continue

      const nextStop = stops[stops.length - 1]
      const prevStop = stops[stops.length - 2]
      const prevRow = shiftTableRows[i - 1]

      // Addding to legs array
      const legStartTime = prevStop.startTime.add(prevStop.duration, 'minutes')
      const legDuration = timeHhMmToMins(row.transitTime) || 0
      const idleLegStartTime = legStartTime.add(legDuration, 'minutes')
      const idleLegEndTime = nextStop.startTime
      const idleLegDuration = idleLegEndTime.diff(idleLegStartTime, 'minutes')
      
      legs.push({
        id: `leg-${i}`,
        shiftId: row.shiftId,
        prevStopId: prevStop.id,
        startTime: legStartTime,
        duration: legDuration,
        transitDistance: row.transitDistance,
        isDroppable: prevRow.isDroppable,
      })

      if (idleLegDuration > 0) {
        legs.push({
          id: `idle-leg-${i}`,
          shiftId: row.shiftId,
          prevStopId: prevStop.id,
          startTime: idleLegStartTime,
          duration: idleLegDuration,
          isIdleLeg: true,
        })
      }
    }

    return {
      id: shiftTable.shiftId,
      scheduleName: shiftTable.scheduleName,
      scheduleId: shiftTable.runsheetId,
      runName: shiftTable.shiftName,
      color: shiftTable.shiftUiColor,
      timeline: {
        id: 'load 1', //TODO: Map the loads into different timelines
        stops: stops,
        travelLegs: legs,
      },
    }
  })

  if (rows.length === 0) {
    minStartTime = dayjs().startOf('day')
    maxEndTime = dayjs().endOf('day')
  }

  return {
    rows: rows.filter(row => row !== null) as GanttRow[],
    isReadOnly: false,
    isSolverRunning: false,
    minStartTime,
    maxEndTime,
  }
}